import React from 'react'
import { withStyles } from '@material-ui/core'
import classNames from 'classnames'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Dialog from '../../../components/Dialog/Dialog'
import InputTokenDialog from '../../../components/Dialog/InputTokenDialog'
import TokenDialog from '../../../components/Dialog/TokenDialog'
import ImpressaoHtml from '../../../components/Impressao/ImpressaoHtml'
import Notification from '../../../components/Notification'
import PageTitle from '../../../components/PageTitle/PageTitle'
import { buildUrlDownload } from '../../../config/config'
import { possuiTokenValido } from '../../../services/DocumentoService'
import localStorageService, { ACCESS_TOKEN_KEY } from '../../../services/storage'
import { findUnidadeLogada } from '../../../services/UnidadeService'
import { getProfissionalSaudeLogado } from '../../../services/UsuarioService'
import { TypeModalEnumTGO } from '../../../stores/TGO.store'
import Colors from '../../../template/Colors'
import TGOGuiaSimples from '../../../template/pdf/TGOGuiaSimples/TGOGuiaSimples'
import { checkUserRole } from '../../../utils/checkUserRole'
import InformacoesImportantesModal from '../InformacoesImportantes/InformacoesImportantesModal'
import ActionButtons from './components/ActionButtons'
import InformationsGuiaTGO from './components/InformationsGuiaTGO'
import ListagemLateral from './components/ListagemLateral/ListagemLateral'
import { getLogoUnidade } from './utils/getLogo'
import { renderPrintPdf } from './utils/renderPrintPdf'

const formErrorsDefault = {
  nome: false,
  indicacaoClinica: false,
}

const GuiaTGOModal = observer(
  ({
    classes,
    handleClose,
     onSaved,
     screen,
    sujeitoAtencao,
    openModal,
    TGOStore,
    profissionalSaudeStore,
    configuracaoImpressaoStore,
    unidadeStore,
    prontuarioStore,
    readOnly,
    history,
    loadModelos,
  }) => {
    const [showDadosImportantes, setShowDadosImportantes] = useState(false)
    const [loadingButtonSave, setLoadingButtonSave] = useState(false)
    const [, setUnidadeLogada] = useState(null)
    const [isPrintMustache, setIsPrintMustache] = useState(false)
    const [loadingButtonImpressao, setLoadingButtonImpressao] = useState(false)
    const [urlLogo, setUrlLogo] = useState('')
    const [openTokenDialog, setOpenTokenDialog] = useState(false)
    const [tokenDialogLoading, setTokenDialogLoading] = useState(false)
    const [openInputTokenDialog, setOpenInputTokenDialog] = useState(false)
    const [token, setToken] = useState('')
    const [tokenError, setTokenError] = useState(false)
    const [finishingSignedPrint, setFinishingSignedPrint] = useState(false)
    const [formErrors, setFormErrors] = useState(formErrorsDefault)
    const [pdfToPrint, setPdfToPrint] = useState(null)

    const initialize = async () => {
      const accessToken = await localStorageService.get(ACCESS_TOKEN_KEY)
      TGOStore.accessToken = accessToken

      await loadSugestoes()
      await handleEntradaProntuario()
      await loadAuxiliaryData()

      TGOStore.objView.sujeitoAtencao = sujeitoAtencao

      const roleEntradaProntuarioRead = checkUserRole('ROLE_ENTRADA_PRONTUARIO_READ')
      const hasInfoImportantes = sujeitoAtencao?.informacoesImportantes?.length > 0
      setShowDadosImportantes(roleEntradaProntuarioRead && hasInfoImportantes)
    }

    const loadSugestoes = async () => {
      TGOStore.sugestoesPagAtual = 0
      TGOStore.sugestoes = []

      await TGOStore.findAllGuiaServicoTussViewQuantidadeUso({
        pageableDTO: {
          pageSize: 10,
          pageNumber: TGOStore.sugestoesPagAtual,
          sortDir: 'DESC',
          sortField: 'quantidadeUso',
        },
      })
    }

    const handleEntradaProntuario = async () => {
      const entradaId = TGOStore.entradaProntuario?.id
      if (!entradaId) return

      try {
        TGOStore.readOnly = true
        const guiaServico = await TGOStore.findGuiaServicoByIdEntradaProntuario(entradaId)

        TGOStore.guiaServico = guiaServico
        TGOStore.objView = {
          ...TGOStore.objView,
          observacao: guiaServico?.observacao,
          guiaServicoTgoTussViews: guiaServico?.guiaServicoTGOTussViews,
          indicacaoClinica: guiaServico?.cid10Subcategoria && {
            label: guiaServico.cid10Subcategoria.descricaoAbreviada,
            value: guiaServico.cid10Subcategoria.id,
          },
        }
      } catch (error) {
        TGOStore.guiaServico = null
      } finally {
        TGOStore.entradaProntuario = null
      }
    }

    const loadAuxiliaryData = async () => {
      getLogo()
      await Promise.all([
        profissionalSaudeStore.carregaProfissionalSaude(),
        prontuarioStore.checkUseDigitalSignature(),
      ])
    }

    const verifyShowDadosImportantes = () => {
      const roleEntradaProntuarioRead = checkUserRole('ROLE_ENTRADA_PRONTUARIO_READ')
      setShowDadosImportantes(
        roleEntradaProntuarioRead && sujeitoAtencao?.informacoesImportantes?.length > 0
      )
    }

    const selectPaperClass = useCallback(() => {
      if (readOnly) {
        return showDadosImportantes ? classes.paperSemModelosComInfo : classes.paperSemModelos
      }

      return showDadosImportantes ? classes.paperFullSize : classes.paper
    }, [readOnly, showDadosImportantes])

    const getLogo = async () => {
      const logo = getLogoUnidade({ sujeitoAtencao, unidadeStore })
      setUrlLogo(logo)
    }

    const handlePrint = async tipo => {
      const { utilizaAssinaturaDigital } = profissionalSaudeStore.profissionalSaude
      const { assinaturaDigitalObrigatoria } = prontuarioStore

      try {
        setLoadingButtonImpressao(true)

        let guiaServico = TGOStore.guiaServico

        if (TGOStore.objView?.guiaServicoTgoTussViews?.length === 0) {
          throw Error('Informe o guia de serviço tgo Tuss')
        }

        if (!TGOStore.readOnly) {
          const guiaTGO = await TGOStore.createGuiaServico(TGOStore.objView)
          guiaServico = await TGOStore.findGuiaServicoById(guiaTGO.id)
          TGOStore.guiaServico = guiaServico
        }

        if (!guiaServico && tipo !== 'guiaSimples') return

        TGOStore.readOnly = true
        if (tipo === 'guiaSimples') {
          await configuracaoImpressaoStore.getConfig('OUTROS')
          const unidadeLogada = await findUnidadeLogada()
          const profissionalLogado = await getProfissionalSaudeLogado()
          const openSignDialog =
            (utilizaAssinaturaDigital || assinaturaDigitalObrigatoria) &&
            profissionalLogado?.tipo === 1

          if (openSignDialog) {
            setUnidadeLogada(unidadeLogada)
            setOpenTokenDialog(true)
          } else {
            handlePrintUnsigned()
          }
        } else {
          TGOStore.printing = true
        }
      } catch (error) {
        TGOStore.openNotification('Informe o guia de serviço tgo Tuss', 'error')
      } finally {
        setLoadingButtonImpressao(false)
      }
    }

    const handlePrintUnsigned = async () => {
      try {
        if (!TGOStore.guiaServico?.id) {
          const guia = {
            ...TGOStore.objView,
            sujeitoAtencao: this.props.sujeitoAtencao,
          }

          const response = await TGOStore.createGuiaServico(guia)
          const guiaTGO = await TGOStore.findGuiaServicoById(response.id)
          TGOStore.guiaServico = guiaTGO
        }
      } catch (error) {
        console.error(error)
      }
      setTokenDialogLoading(true)
      setIsPrintMustache(true)
    }

    const handleTokenChange = e => {
      setToken(e.target.value)
    }

    const handleChooseSigned = async () => {
      setOpenTokenDialog(false)
      try {
        const response = await possuiTokenValido()
        const { possuiTokenAssinaturaValido: validToken } = response.data.data

        if (validToken) {
          await handleFinishSignedPrint(validToken)
        } else {
          setOpenInputTokenDialog(true)
        }
      } catch (e) {
        console.error(e)
      }
    }

    const handleFinishSignedPrint = async (validToken = false) => {
      setFinishingSignedPrint(true)
      try {
        const guiaServico = await TGOStore.printSigned(token, validToken)
        const urlDownload = guiaServico.entradaProntuario.urlDownload
        const completeURL = buildUrlDownload(urlDownload, TGOStore.accessToken)
        window.open(completeURL, '_blank')

        resetPrintStates()
      } catch (e) {
        console.error(e.message)
        resetPrintStates(true)
      }
    }

    const handlePrintMustache = () => {
      resetPrintStates()
      setLoadingButtonImpressao(false)
    }

    const handleCloseTokenDialog = () => {
      resetPrintStates()
    }

    const titleTipo = () => {
      const { typeModal, readOnly } = TGOStore

      if (typeModal === 'modalEdit') {
        return `${screen} - Editar modelo TGO`
      }

      if (typeModal === 'novoModal') {
        return `${screen} - Novo modelo TGO`
      }

      const acao = readOnly ? 'Visualizar' : 'Novo'
      return `${screen} - ${acao} TGO`
    }

    const getTitleModal = () => {
      const { typeModal } = TGOStore

      const titles = {
        [TypeModalEnumTGO.TGO_MODAL]: 'Guia GTO',
        [TypeModalEnumTGO.EDIT_MODAL]: 'Editar modelo',
      }

      return titles[typeModal] || 'Novo modelo GTO'
    }

    const getErrorMessage = form => {
      if (!form.nome || form.ativo === undefined || !form.indicacaoClinica) {
        setFormErrors({
          nome: !form.nome,
          indicacaoClinica: !form.indicacaoClinica,
        })
        return 'Favor preencher todos os campos.'
      } else {
        setFormErrors(formErrorsDefault)
      }

      if (form.guiaServicoModeloTgoTussViews.length === 0) {
        return 'Adicione pelo menos um procedimento.'
      }
    }

    const handleCopyTGO = () => {
      TGOStore.readOnly = false
    }

    const resetPrintStates = (withError = false) => {
      setIsPrintMustache(false)
      setTokenDialogLoading(false)
      setOpenTokenDialog(false)
      setOpenInputTokenDialog(false)
      setLoadingButtonSave(false)
      setToken('')
      setFinishingSignedPrint(false)
      setTokenError(withError)
      setLoadingButtonImpressao(false)
    }

    const isValidCreate = modelo => {
      return (
        !!modelo.nome &&
        modelo.ativo !== undefined &&
        modelo.guiaServicoModeloTgoTussViews?.length > 0
      )
    }

    const isValidUpdate = modelo => {
      return (
        !!modelo.nome &&
        modelo.ativo !== undefined &&
        modelo.guiaServicoModeloTgoTussViews?.length > 0 &&
        modelo.indicacaoClinica
      )
    }

    const handleSave = async () => {
      try {
        setLoadingButtonSave(true)

        if (TGOStore.idModeloSelecionado) {
          let indicacaoClinica
          if (TGOStore.objView?.indicacaoClinica?.value) {
            indicacaoClinica = TGOStore.objView.indicacaoClinica
          }

          const novoModelo = {
            id: TGOStore.idModeloSelecionado,
            nome: TGOStore.nomeModelo,
            ativo: TGOStore.statusNovoModelo,
            observacao: TGOStore.objView.observacao,
            guiaServicoModeloTgoTussViews: TGOStore.objView.guiaServicoTgoTussViews.map(item => {
              return {
                ...item,
                localOdontograma: {
                  id: item.localOdontograma?.local ?? item.localOdontograma?.id,
                },
                face: item.face?.label ?? item.face,
              }
            }),
            indicacaoClinica,
          }
          const errors = getErrorMessage(novoModelo)

          if (isValidUpdate(novoModelo)) {
            await TGOStore.updateGuiaServicoModelo(novoModelo)
            TGOStore.resetModelo()
            TGOStore.changeFieldsStore({
              openModal: false,
              pageNumberModelo: 0,
              modelosConfig: [],
              idModeloSelecionado: null,
              tipoListagemSelecionada: 'sugestoes',
            })
            loadModelos && loadModelos()
          } else {
            TGOStore.openNotification(errors, 'error')
          }
        } else {
          const novoModelo = {
            nome: TGOStore.nomeModelo,
            observacao: TGOStore.objView.observacao,
            ativo: TGOStore.statusNovoModelo,
            guiaServicoModeloTgoTussViews: TGOStore.objView.guiaServicoTgoTussViews.map(item => {
              return {
                ...item,
                localOdontograma: {
                  id: item.localOdontograma?.local,
                },
                face: item.face?.label ?? item.face,
              }
            }),
            indicacaoClinica: TGOStore.objView.indicacaoClinica?.value,
          }
          const errors = getErrorMessage(novoModelo)

          if (isValidCreate(novoModelo)) {
            await TGOStore.createGuiaServicoModelo(novoModelo)
            TGOStore.resetModelo()
            if (TGOStore.isProntuario) {
              TGOStore.changeFieldsStore({
                typeModal: TypeModalEnumTGO.TGO_MODAL,
                observacaoTemporaria: '',
                guiaServicoTussViewsTemporaria: [],
                indicacaoClinicaTemporaria: null,
              })

              TGOStore.changeFieldsObjViewStore({
                observacao: TGOStore.observacaoTemporaria || '',
                guiaServicoTgoTussViews: TGOStore.guiaServicoTussViewsTemporaria || [],
                indicacaoClinica: TGOStore.indicacaoClinicaTemporaria || null,
              })
            } else {
              TGOStore.openModal = false
            }

            TGOStore.pageNumberModelo = 0

            if (!!loadModelos) {
              TGOStore.modelosConfig = []
              loadModelos()
            }
          } else {
            TGOStore.openNotification(errors, 'error')
          }
        }
      } catch (errors) {
        TGOStore.openNotification('Não foi possível salvar o modelo TGO', 'error')
      } finally {
        setLoadingButtonSave(false)
      }
    }

    const paperClass = useMemo(() => {
      return selectPaperClass()
    }, [readOnly, showDadosImportantes])

    const signedUrl = useMemo(() => {
      return TGOStore.guiaServico?.assinado
        ? buildUrlDownload(TGOStore.guiaServico.entradaProntuario.urlDownload, TGOStore.accessToken)
        : null
    }, [TGOStore.guiaServico])

    useEffect(() => {
      verifyShowDadosImportantes()
      initialize()
    }, [])

    return (
      <>
        <PageTitle title={titleTipo()} />
        <Dialog
          id="cfg-gtm-dlg-mod"
          classes={{
            paper: paperClass,
          }}
          open={openModal}
          fullWidth
        >
          {showDadosImportantes && (
            <div id="cfg-gtm-div-inf">
              <InformacoesImportantesModal minimizable withoutBorderRadius />
            </div>
          )}
          <div className={classes.guiaTGOModalContent}>
            <div
              className={classNames(
                classes.wrapperContent,
                !showDadosImportantes && classes.wrapperContentSemDados,
                TGOStore.readOnly && classes.wrapperContentReadOnly,
                !showDadosImportantes && TGOStore.readOnly && classes.wrapperContentReadOnlySemDados
              )}
            >
              <div className={classes.tituloButtons}>
                <div className={classes.titulo}>{getTitleModal()}</div>
                <ActionButtons
                  readOnly={readOnly}
                  signedUrl={signedUrl}
                  guiaServico={TGOStore.guiaServico}
                  typeModal={TGOStore.typeModal}
                  loadingSave={loadingButtonSave}
                  loadingPrint={loadingButtonImpressao}
                  onSave={handleSave}
                  handlePrint={handlePrint}
                  handleClose={handleClose}
                  handleCopyTGO={handleCopyTGO}
                />
              </div>
              <InformationsGuiaTGO formErrors={formErrors} />
            </div>
            {!TGOStore.readOnly && <ListagemLateral />}
            {TGOStore.printing &&
              !loadingButtonImpressao &&
              renderPrintPdf({
                guiaServico: TGOStore.guiaServico,
                urlLogo,
                pdfToPrint,
                onAfterPrint: (base64) => {
                  if (base64) {
                    setPdfToPrint(base64)
                  } else {
                    setPdfToPrint(null)
                    setLoadingButtonImpressao(false)
                    TGOStore.printing = false
                    typeof onSaved === 'function' && onSaved();
                    handleClose()
                  }
                },
              })}
          </div>
        </Dialog>
        <Notification
          close={e => TGOStore.closeNotification(e)}
          reset={() => TGOStore.resetNotification()}
          isOpen={TGOStore.notification.isOpen}
          variant={TGOStore.notification.variant}
          message={TGOStore.notification.message}
        />
        {isPrintMustache && (
          <ImpressaoHtml
            isPrintMustache={isPrintMustache}
            sujeitoAtencaoId={sujeitoAtencao.id}
            handlePrintMustache={handlePrintMustache}
            htmlStringComponent={<TGOGuiaSimples guiaServico={TGOStore.guiaServico} />}
          />
        )}
        <TokenDialog
          open={openTokenDialog}
          handleClose={handleCloseTokenDialog}
          onCancel={handlePrintUnsigned}
          onOk={handleChooseSigned}
          isLoading={tokenDialogLoading}
        />
        <InputTokenDialog
          open={openInputTokenDialog}
          onChange={handleTokenChange}
          onOk={() => handleFinishSignedPrint(false)}
          onCancel={handleCloseTokenDialog}
          onClose={handleCloseTokenDialog}
          loading={finishingSignedPrint}
          error={tokenError}
          inputValue={token}
          canPrintSigned={profissionalSaudeStore.canPrintSigned}
          history={history}
        />
      </>
    )
  }
)

const paperBase = {
  display: 'flex',
  flexDirection: 'row',
  overflow: 'unset',
  height: 580,
  borderRadius: 10,
}

const styles = theme => ({
  paper: {
    ...paperBase,
    background: Colors.commons.grayLight,
  },
  paperFullSize: {
    ...paperBase,
    background: 'transparent',
    width: 'auto',
    maxWidth: '1200px',
  },
  paperSemModelosComInfo: {
    ...paperBase,
    background: 'transparent',
    maxWidth: '962px',
  },
  paperSemModelos: {
    ...paperBase,
    maxWidth: '744px',
    background: Colors.commons.grayLight,
  },
  guiaTGOModalContent: {
    display: 'flex',
    width: '100%',
    maxWidth: '960px',
  },
  wrapperContent: {
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    padding: '0px 16px',
    width: '100%',
    maxWidth: '600px',
  },
  wrapperContentSemDados: {
    borderRadius: '16px 0px 0px 16px',
  },
  wrapperContentReadOnly: {
    borderRadius: '0px 16px 16px 0px',
  },
  wrapperContentReadOnlySemDados: {
    borderRadius: '16px',
  },
  titulo: {
    fontSize: 24,
    fontWeight: 700,
    color: Colors.commons.secondary,
  },
  tituloButtons: {
    background: '#fff',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 24px',
  },
})

const GuiaTGOModalWithStyles = withStyles(styles)(GuiaTGOModal)
export default inject(
  'TGOStore',
  'profissionalSaudeStore',
  'unidadeStore',
  'configuracaoImpressaoStore',
  'prontuarioStore'
)(GuiaTGOModalWithStyles)
